// Client-side tree helpers for the Flow Builder. The server-side
// TreeValidator remains authoritative; these only keep the drafts sane.

export const STEP_TYPES = [
  'SendMessage',
  'QuestionStep',
  'BranchStep',
  'Jump',
  'AssignTo',
  'WaitStep',
  'CloseConversation',
  'CsatStep',
  'SendAttachment',
  'DateTime',
  'UpdateContactField',
  'UpdateContactTags',
  'UpdateLifecycleStage',
  'AddComment',
  'OpenConversation',
  'TriggerByAnotherFlow',
  'HTTPRequest',
  'WhatsAppList',
  'AIStep',
  'SetVariable',
];

const uid = () => `n_${Math.random().toString(36).slice(2, 10)}`;

const answerBranch = name => ({
  id: uid(),
  pId: null,
  name,
  customType: 'AnswerStep',
  data: {},
  childs: [],
});

const conditionBranch = (name, conditions) => ({
  id: uid(),
  pId: null,
  name,
  customType: 'ConditionStep',
  data: { conditions },
  childs: [],
});

export const buildStepNode = type => {
  const node = {
    id: uid(),
    pId: null,
    name: type,
    customType: type,
    data: {},
    childs: [],
  };
  switch (type) {
    case 'SendMessage':
      node.data = { payload: [{ message: { type: 'text', text: '' } }] };
      break;
    case 'QuestionStep':
      node.data = {
        question: {
          type: 'text',
          text: '',
          options: [],
          presentation: 'text',
          validation: { type: 'none' },
        },
        saveResponse: { variable: '' },
        timeout: { hasTimeout: false, unit: 'minutes', value: 10 },
      };
      node.childs = [
        answerBranch('Success'),
        answerBranch('Failure: Invalid'),
        answerBranch('Failure: Timeout'),
      ];
      break;
    case 'BranchStep':
      node.childs = [
        conditionBranch('Branch 1', [
          {
            attribute_key: 'variable',
            variable: '',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          },
        ]),
        conditionBranch('Else', []),
      ];
      break;
    case 'Jump':
      node.data = { stepId: '', maxJumps: 3 };
      break;
    case 'AssignTo':
      node.data = {
        assignTo: 'workspace',
        assignmentLogic: 'RoundRobin',
        onlyOnlineUsers: false,
        userId: null,
        teamId: null,
      };
      break;
    case 'WaitStep':
      node.data = { unit: 'minutes', value: 5 };
      break;
    case 'DateTime':
      node.data = { at: '', unit: 'minutes', value: 5 };
      break;
    case 'SendAttachment':
      node.data = { attachmentId: null, caption: '' };
      break;
    case 'UpdateContactField':
      node.data = { key: '', value: '' };
      break;
    case 'UpdateContactTags':
      node.data = { operation: 'add', target: 'contact', labels: [] };
      break;
    case 'UpdateLifecycleStage':
      node.data = { stageId: null, clear: false, reason: '' };
      break;
    case 'AddComment':
      node.data = { text: '' };
      break;
    case 'TriggerByAnotherFlow':
      node.data = { flowId: null };
      break;
    case 'HTTPRequest':
      node.data = {
        method: 'GET',
        url: '',
        headers: {},
        body: '',
        responseMapping: {},
        failureBranch: true,
        errorVariable: 'http_error',
      };
      node.childs = [answerBranch('Success'), answerBranch('Failure')];
      break;
    case 'SetVariable':
      node.data = { key: '', value: '' };
      break;
    case 'WhatsAppList':
      node.data = { title: '', body: '', items: [] };
      break;
    case 'AIStep':
      node.data = { prompt: '', outputVariable: 'ai_result' };
      break;
    default:
      node.data = {};
  }
  return node;
};

export const walkTree = (node, visit) => {
  if (!node) return;
  visit(node);
  (node.childs || []).forEach(child => walkTree(child, visit));
};

export const countNodes = tree => {
  let count = 0;
  walkTree(tree, () => {
    count += 1;
  });
  return count;
};

export const findNode = (tree, id) => {
  let found = null;
  walkTree(tree, node => {
    if (node.id === id) found = node;
  });
  return found;
};

export const findParent = (tree, id) => {
  let parent = null;
  walkTree(tree, node => {
    if ((node.childs || []).some(child => child.id === id)) parent = node;
  });
  return parent;
};

// Nodes whose direct children are named branches rather than the next step.
export const BRANCHING_TYPES = ['QuestionStep', 'BranchStep'];
export const isBranchingNode = node =>
  BRANCHING_TYPES.includes(node?.customType) ||
  (node?.customType === 'HTTPRequest' && node?.data?.failureBranch);

// ---- moving a subtree -------------------------------------------------------
// Dragging in a tree is re-parenting, not repositioning: the node and
// everything under it leave one slot and arrive in another. These helpers keep
// that surgery in one place so the component never hand-edits `childs`.

// True when `nodeId` sits anywhere beneath `ancestorId`. Dropping a node into
// its own subtree would detach that subtree from the tree entirely and leave a
// cycle behind, so every move is checked against this first.
export const isDescendant = (tree, ancestorId, nodeId) => {
  const ancestor = findNode(tree, ancestorId);
  if (!ancestor) return false;
  let found = false;
  walkTree(ancestor, n => {
    if (n.id === nodeId) found = true;
  });
  return found;
};

// A node may be dragged only if it is a real step: never the Trigger, and never
// a branch container (AnswerStep / ConditionStep), whose identity belongs to the
// Question or Branch that owns it.
export const isDraggableNode = node =>
  Boolean(node) &&
  node.customType !== 'Trigger' &&
  !['AnswerStep', 'ConditionStep'].includes(node.customType);

// Remove a node from its parent and return it. A linear node hands its single
// child up to the parent so the chain below it survives the move - the same rule
// deletion uses, because a move is a detach plus an attach.
export const detachNode = (tree, id) => {
  const parent = findParent(tree, id);
  if (!parent) return null;
  const index = parent.childs.findIndex(c => c.id === id);
  if (index === -1) return null;
  const node = parent.childs[index];
  const isBranching = isBranchingNode(node);
  if (node.childs?.length === 1 && !isBranching) {
    const promoted = node.childs[0];
    parent.childs.splice(index, 1, promoted);
    node.childs = [];
  } else {
    parent.childs.splice(index, 1);
  }
  return node;
};

// Attach `node` under `parentId`, pushing whatever was there down beneath it.
//
// The subtlety is where "beneath it" lives when the arriving node branches. A
// Question or Branch owns its named branches as its direct children, so the
// subtree already at this slot cannot become another child of it - that would
// leave a linear parent holding two children, which FlowNode renders as one
// (it draws childs[0] only, so the rest vanish from the canvas) and which
// TreeValidator rejects on save. The subtree belongs on the first branch, the
// success path, which is what a reader of the flow would expect.
export const attachNode = (tree, parentId, node) => {
  const parent = findNode(tree, parentId);
  if (!parent) return false;
  if (!parent.childs) parent.childs = [];

  // Branch containers are the only legal children of a branching node.
  if (isBranchingNode(parent)) return false;
  // Already invalid - do not compound it.
  if (parent.childs.length > 1) return false;

  if (!parent.childs.length) {
    parent.childs = [node];
    return true;
  }

  const existing = parent.childs[0];
  if (isBranchingNode(node)) {
    const firstBranch = node.childs?.[0];
    // Refuse rather than overwrite: a branch is itself linear, so if the
    // success path is occupied there is nowhere for the existing subtree to go
    // without discarding one of them.
    if (!firstBranch || firstBranch.childs?.length) return false;
    firstBranch.childs = [existing];
  } else {
    node.childs = [existing];
  }
  parent.childs = [node];
  return true;
};

// Will `attachNode` accept this node at this slot? Pure - changes nothing.
//
// Exists so a move can be refused BEFORE the subtree is detached. Detaching and
// then undoing on failure does not restore the original tree: the parent's shape
// has changed by then, so re-attaching can place the node differently from where
// it started. Check first, mutate once.
export const canAttach = (tree, parentId, node) => {
  const parent = findNode(tree, parentId);
  if (!parent || !node) return false;
  if (isBranchingNode(parent)) return false;
  const childs = parent.childs || [];
  if (childs.length > 1) return false;
  if (!childs.length) return true;
  if (!isBranchingNode(node)) return true;
  const firstBranch = node.childs?.[0];
  return Boolean(firstBranch) && !firstBranch.childs?.length;
};

// The whole move, refusing anything that would corrupt the tree.
export const moveNode = (tree, dragId, targetParentId) => {
  if (!tree || !dragId || !targetParentId) return false;
  if (dragId === targetParentId) return false;
  const node = findNode(tree, dragId);
  if (!isDraggableNode(node)) return false;
  // A Question or Branch owns named containers as its direct children. Dropping
  // a step straight onto one would make that step a sibling of Success/Failure
  // instead of living inside a branch, which silently breaks the split. The UI
  // never offers a bare insert point under these either - a branch is the only
  // legal home.
  const target = findNode(tree, targetParentId);
  if (isBranchingNode(target)) return false;
  // into its own subtree, or back where it already is
  if (isDescendant(tree, dragId, targetParentId)) return false;
  const currentParent = findParent(tree, dragId);
  if (currentParent?.id === targetParentId) return false;
  // Everything that can refuse this move is decided here, while the tree is
  // still untouched.
  if (!canAttach(tree, targetParentId, node)) return false;
  const detached = detachNode(tree, dragId);
  if (!detached) return false;
  return attachNode(tree, targetParentId, detached);
};

export const allNodeIds = tree => {
  const ids = [];
  walkTree(tree, node =>
    ids.push({ id: node.id, label: `${node.customType} (${node.name})` })
  );
  return ids;
};
